import { Button, assets } from '@round/whitelabel-ui';
import { useProjectCampaigns } from '../hooks/useProjectCampaigns';
import useInvalidateProjectCampaigns from '../hooks/useInvalidateProjectCampaigns';
import CreateYoutubeCampaignModal from './CreateYoutubeCampaignModal/CreateYoutubeCampaignModal';
import { useMemo, useState } from 'react';
import { creatorbase } from '@round/api';
import UpdateYoutubeCampaignModal from './UpdateYoutubeCampaignModal/UpdateYoutubeCampaignModal';
import ProjectCampaignsTable, { CampaignTableRow } from '../components/ProjectCampaignsTable/ProjectCampaignsTable';
import styles from './YoutubeCampaigns.module.css';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import useNonNullContext from 'Hooks/useNonNullContext';
import useProjectDetails from '../useProjectDetails';
import { useProjectCampaignsStats } from '../hooks/useProjectCampaignsStats';
import { mapStringListToArray, showNotification } from 'helpers';
import { ExpandedState } from '@tanstack/react-table';
import useCreateProjectDetailsPost from '../hooks/posts/useCreateProjectDetailsPost';
import YoutubePosts from '../components/PostsTables/YoutubePostsTable/YoutubePosts';
import CreatePostModal from '../Posts/CreatePostModal/CreatePostModal';
import BulkCreatePostModal from '../Posts/BulkCreatePostModal/BulkCreatePostModal';
import useUrlState from 'Hooks/useUrlState';
import { isNumber, isNotNullOrUndefined } from 'utility/utility';
import DeleteCampaignModal from '../components/DeleteCampaignModal/DeleteCampaignModal';
import { useReorderProjectCampaigns } from '../hooks/useReorderProjectCampaigns';
import useCurrentUserClients from 'Modules/Plans/Clients/hooks/useCurrentUserClients';
import CampaignBriefModal from '../components/CampaignBriefModal/CampaignBriefModal';
import { formatBriefDataToWhatsAppMarkdown } from '../helpers/copyToClipboard.helpers';
import copy from 'copy-to-clipboard';
import PurchaseOrderAllocationModal from 'Modules/Plans/Finance/PurchaseOrders/PurchaseOrderAllocationModal/PurchaseOrderAllocationModal';
import { PostUrlClipboardButton } from 'Modules/Plans/CopyToClipboard/PostUrlTable';
import useCampaignPurchaseOrders from '../hooks/useCampaignPurchaseOrders';
import useCopyYoutubeReportToClipboard from '../hooks/copyToClipboard/useCopyYoutubeReportToClipboard';
import useCopyYoutubeProposalToClipboard from '../hooks/copyToClipboard/useCopyYoutubeProposalToClipboard';
import useCopyYoutubePostUrlsToClipboard from '../hooks/copyToClipboard/useCopyYoutubePostUrlsToClipboard';

const { Plus: PlusIcon } = assets;

type UrlState = Partial<{
    expanded: string;
    backupExpanded: string;
}>;

const YoutubeCampaigns = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>();

    const { currencies } = useNonNullContext(OptionsContext);

    const { project, users, brand, team } = useProjectDetails();

    const { data, isError: hasCampaignsError, isInitialized: areCampaignsInitialized } = useProjectCampaigns('youtube');
    const youtubeCampaigns = data?.results;
    const invalidateProjectCampaigns = useInvalidateProjectCampaigns();

    const campaignIds = youtubeCampaigns?.map((c) => c.id) ?? [];

    const { data: campaignsStats, loadingCampaignIds: campaignStatsLoading } = useProjectCampaignsStats(campaignIds);

    const campaignPurchaseOrders = useCampaignPurchaseOrders({ campaignIds: campaignIds });

    const reorderCampaigns = useReorderProjectCampaigns('youtube');

    const tableRows: CampaignTableRow[] = useMemo(() => {
        return (
            youtubeCampaigns?.map((campaign) => {
                const currency = currencies.find((c) => c.id === campaign.currency_id);
                const teamMembers = users?.results.filter((user) => campaign.team_members.includes(user.id)) ?? [];
                const stats = campaignsStats[campaign.id]?.data ?? null;
                const purchaseOrders =
                    campaignPurchaseOrders.data
                        ?.filter((i) => i.campaignId === campaign.id)
                        .map((i) => i.purchaseOrder)
                        .filter(isNotNullOrUndefined) ?? [];

                return { ...campaign, teamMembers, currency, stats, purchaseOrders };
            }) ?? []
        );
    }, [currencies, youtubeCampaigns, campaignsStats, users, campaignPurchaseOrders.data]);

    const [isCreateCampaignModalOpen, setIsCreateCampaignModalOpen] = useState(false);
    const [isEditCampaignModalOpen, setIsEditCampaignModalOpen] = useState(false);
    const [isEditBriefModalOpen, setIsEditBriefModalOpen] = useState(false);
    const [campaignToEdit, setCampaignToEdit] = useState<CampaignTableRow | null>(null);
    const [isConfirmDeleteCampaignModalOpen, setIsConfirmDeleteCampaignModalOpen] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState<CampaignTableRow | null>(null);

    const expandedState = useMemo(() => {
        const expanded = urlState?.expanded;
        if (!expanded) {
            return {};
        }

        return expanded.split(',').reduce(
            (acc, campaignId) => {
                acc[campaignId] = true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [urlState]);

    const setExpandedState = (expanded: ExpandedState) => {
        const expandedKeys = Object.keys(expanded).filter((key) => expanded[key as keyof ExpandedState]);
        setUrlState({ expanded: expandedKeys.join(',') });
    };

    const { createPost } = useCreateProjectDetailsPost();

    const { clients } = useCurrentUserClients();
    const isPaymentEnabled = !!clients.find((c) => c.id === team?.client_id)?.is_payment_enabled;

    const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
    const [isBulkCreatePostModalOpen, setIsBulkCreatePostModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState<CampaignTableRow | null>(null);

    const onCreatePost = (campaign: CampaignTableRow) => {
        setIsCreatePostModalOpen(true);
        setSelectedCampaign(campaign);
    };

    const onBulkCreatePost = (campaign: CampaignTableRow) => {
        setIsBulkCreatePostModalOpen(true);
        setSelectedCampaign(campaign);
    };

    const copyBriefToClipboard = (campaign: CampaignTableRow) => {
        const formattedBriefData = formatBriefDataToWhatsAppMarkdown({
            audioUrl: undefined,
            brandName: brand?.name,
            projectName: project?.name,
            campaignName: campaign.name,
            brief: campaign.brief,
            turnaround: campaign.brief_turnaround,
        });

        const success = copy(formattedBriefData, { format: 'text/plain' });

        if (success) {
            showNotification('Brief copied!', 'info');
        } else {
            showNotification('Could not copy brief', 'error');
        }
    };

    const copyReportTableToClipboard = useCopyYoutubeReportToClipboard();

    const expandedBackupCampaigns = mapStringListToArray(urlState.backupExpanded).map(Number);
    const toggleExpandedBackupCampaigns = (campaignId: number) => {
        if (expandedBackupCampaigns.includes(campaignId)) {
            setUrlState({ backupExpanded: expandedBackupCampaigns.filter((id) => id !== campaignId).toString() });
            return;
        }

        setUrlState({ backupExpanded: expandedBackupCampaigns.concat(campaignId).toString() });
    };

    const copyProposalTableToClipboard = useCopyYoutubeProposalToClipboard();

    const copyPostUrlsToClipboard = useCopyYoutubePostUrlsToClipboard();

    const [allocationCampaignId, setAllocationCampaignId] = useState<number | null>(null);

    return (
        <div className={styles.container}>
            <div className={styles.actions}>
                <PostUrlClipboardButton onClick={() => copyPostUrlsToClipboard(youtubeCampaigns ?? [])} />
                <Button
                    appearance="primary"
                    onClick={() => setIsCreateCampaignModalOpen(true)}
                    iconLeft={<PlusIcon className={styles.plusIcon} />}
                >
                    Campaign
                </Button>
            </div>

            <ProjectCampaignsTable
                data={tableRows}
                onEdit={(c) => {
                    setCampaignToEdit(c);
                    setIsEditCampaignModalOpen(true);
                }}
                onEditBrief={(c) => {
                    setCampaignToEdit(c);
                    setIsEditBriefModalOpen(true);
                }}
                onCopyBrief={copyBriefToClipboard}
                onCopyReport={copyReportTableToClipboard}
                onCopyProposal={copyProposalTableToClipboard}
                onCreatePost={onCreatePost}
                onBulkCreatePost={onBulkCreatePost}
                onCreateCampaign={() => setIsCreateCampaignModalOpen(true)}
                onDeleteCampaign={(campaign) => {
                    setIsConfirmDeleteCampaignModalOpen(true);
                    setCampaignToDelete(campaign);
                }}
                onAllocate={(row) => setAllocationCampaignId(row.id)}
                hasError={hasCampaignsError}
                expandedState={expandedState}
                onExpandedChange={setExpandedState}
                renderSubComponent={(row) => (
                    <YoutubePosts
                        campaign={row.original}
                        isPaymentEnabled={isPaymentEnabled}
                        isBackupExpanded={expandedBackupCampaigns.includes(row.original.id)}
                        onBackupExpandToggle={() => toggleExpandedBackupCampaigns(row.original.id)}
                        onCreatePost={() => onCreatePost(row.original)}
                    />
                )}
                isLoading={!areCampaignsInitialized}
                statsLoading={campaignStatsLoading}
                arePurchaseOrdersLoading={campaignPurchaseOrders.isLoading}
                onDragEnd={reorderCampaigns}
            />

            <CreateYoutubeCampaignModal
                isOpen={isCreateCampaignModalOpen}
                closeModal={() => setIsCreateCampaignModalOpen(false)}
            />

            <UpdateYoutubeCampaignModal
                isOpen={isEditCampaignModalOpen}
                closeModal={() => {
                    setIsEditCampaignModalOpen(false);
                    setCampaignToEdit(null);
                }}
                campaign={campaignToEdit as creatorbase.YoutubeCampaign | null}
            />

            <DeleteCampaignModal
                isOpen={isConfirmDeleteCampaignModalOpen}
                campaign={campaignToDelete}
                onClose={() => {
                    setIsConfirmDeleteCampaignModalOpen(false);
                    setCampaignToDelete(null);
                }}
            />

            <CampaignBriefModal
                isOpen={isEditBriefModalOpen}
                closeModal={() => {
                    setIsEditBriefModalOpen(false);
                    setCampaignToEdit(null);
                }}
                campaign={campaignToEdit}
            />

            <CreatePostModal
                isOpen={isCreatePostModalOpen}
                closeModal={() => {
                    setIsCreatePostModalOpen(false);
                    setSelectedCampaign(null);
                }}
                campaign={youtubeCampaigns?.find((c) => c.id === selectedCampaign?.id)}
                createPost={createPost}
            />

            <BulkCreatePostModal
                isOpen={isBulkCreatePostModalOpen}
                closeModal={() => {
                    setIsBulkCreatePostModalOpen(false);
                    setSelectedCampaign(null);
                }}
                campaign={youtubeCampaigns?.find((c) => c.id === selectedCampaign?.id)}
            />

            <PurchaseOrderAllocationModal
                campaignId={allocationCampaignId}
                isOpen={isNumber(allocationCampaignId)}
                onClose={(hasUpdated) => {
                    if (hasUpdated && allocationCampaignId) {
                        invalidateProjectCampaigns('youtube');
                    }

                    setAllocationCampaignId(null);
                }}
            />
        </div>
    );
};

export default YoutubeCampaigns;
